import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, ShieldCheck, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { LogoMarkStatic } from "@/components/LogoMark";

// BLOCK 35: the login page now handles the two-stage MFA challenge for
// accounts that have MFA enabled (all real admins). First submit sends
// email+password only. If the backend replies 401 "MFA code required"
// we reveal a 6-digit TOTP input on the same form and let the user
// switch to a recovery-code input as a documented fallback.
export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // BLOCK 40 ,  if the user was bounced here from a protected /app/* URL
  // (e.g. clicking "View →" in the FL Wind-Down Digest email while
  // logged out), RequireAuth stashed the original path in state.from.
  // Send them back to it after a successful login instead of the dashboard.
  const redirectTo = location?.state?.from?.pathname
    ? `${location.state.from.pathname}${location.state.from.search || ""}`
    : "/app/dashboard";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [mfaStage, setMfaStage] = useState(false);
  const [useRecovery, setUseRecovery] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [mfaError, setMfaError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMfaError("");
    try {
      const opts = {};
      if (mfaStage) {
        if (useRecovery) {
          if (!recoveryCode.trim()) {
            setMfaError("Enter your recovery code.");
            setBusy(false);
            return;
          }
          opts.recovery_code = recoveryCode.trim();
        } else {
          if (!/^\d{6}$/.test(mfaCode.trim())) {
            setMfaError("Enter the 6-digit code from your authenticator.");
            setBusy(false);
            return;
          }
          opts.mfa_code = mfaCode.trim();
        }
      }
      await login(email, password, opts);
      toast.success("Welcome back");
      const loggedInRole = JSON.parse(localStorage.getItem("ae_user") || "{}")?.role;
      navigate(loggedInRole === "seller" ? "/seller/dashboard" : redirectTo, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === "MFA code required") {
        // Reveal the MFA input on the same form ,  no page transition.
        setMfaStage(true);
        setUseRecovery(false);
        setMfaCode("");
        setRecoveryCode("");
        setMfaError("Enter the 6-digit code from your authenticator app.");
      } else if (detail === "Invalid MFA code") {
        setMfaError(
          useRecovery
            ? "That recovery code is not valid. Each one only works once."
            : "That code did not match. Try again with the current 6-digit code."
        );
      } else {
        toast.error(detail || "Login failed");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#05070A] text-slate-100 relative overflow-hidden grid md:grid-cols-2">
      <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />

      <div className="relative z-10 flex flex-col justify-center px-10 py-12 max-w-xl">
        <Link to="/" className="flex items-center gap-2 mb-12">
          <LogoMarkStatic className="h-20 md:h-[144px]" />
        </Link>

        <div className="text-xs uppercase tracking-widest text-emerald-400 mb-3">Sign in</div>
        <h1 className="font-heading text-4xl tracking-tight">Welcome back.</h1>
        <p className="text-slate-400 mt-2 text-sm">Access your acquisition intelligence dashboard.</p>

        <form onSubmit={submit} className="mt-8 space-y-4 max-w-md" data-testid="login-form">
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1.5">Email</label>
            <input
              data-testid="login-email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (mfaStage) {
                  // Any credential change invalidates the current MFA stage.
                  setMfaStage(false);
                  setMfaCode("");
                  setRecoveryCode("");
                  setMfaError("");
                }
              }}
              required
              disabled={mfaStage}
              className="w-full bg-[#0E1116] border border-white/10 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-500/60 focus:ring-1 focus:ring-emerald-500/40 disabled:opacity-60"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1.5">Password</label>
            <input
              data-testid="login-password"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (mfaStage) {
                  setMfaStage(false);
                  setMfaCode("");
                  setRecoveryCode("");
                  setMfaError("");
                }
              }}
              required
              disabled={mfaStage}
              className="w-full bg-[#0E1116] border border-white/10 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-500/60 focus:ring-1 focus:ring-emerald-500/40 disabled:opacity-60"
            />
          </div>

          {mfaStage && (
            <div
              data-testid="login-mfa-panel"
              className="ae-card p-4 border border-emerald-500/25 bg-emerald-500/[0.03]"
            >
              <div className="flex items-center gap-2 mb-3">
                {useRecovery ? (
                  <KeyRound className="h-4 w-4 text-emerald-400" />
                ) : (
                  <ShieldCheck className="h-4 w-4 text-emerald-400" />
                )}
                <div className="text-[10px] uppercase tracking-widest text-emerald-400">
                  {useRecovery ? "Recovery code" : "Two-factor code"}
                </div>
              </div>

              {!useRecovery ? (
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1.5">
                    6-digit code
                  </label>
                  <input
                    data-testid="login-mfa-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="\d{6}"
                    maxLength={6}
                    autoFocus
                    value={mfaCode}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, "").slice(0, 6);
                      setMfaCode(v);
                      if (mfaError) setMfaError("");
                    }}
                    placeholder="123456"
                    className="w-full bg-[#0E1116] border border-white/10 rounded-sm px-3 py-2.5 text-lg font-mono tracking-[0.5em] text-center focus:outline-none focus:border-emerald-500/60 focus:ring-1 focus:ring-emerald-500/40"
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1.5">
                    One-time recovery code
                  </label>
                  <input
                    data-testid="login-recovery-code"
                    type="text"
                    autoComplete="off"
                    autoFocus
                    value={recoveryCode}
                    onChange={(e) => {
                      setRecoveryCode(e.target.value);
                      if (mfaError) setMfaError("");
                    }}
                    placeholder="XXXX-XXXX-XXXX"
                    className="w-full bg-[#0E1116] border border-white/10 rounded-sm px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-emerald-500/60 focus:ring-1 focus:ring-emerald-500/40"
                  />
                  <div className="text-[11px] text-amber-400/80 mt-2">
                    Recovery codes are single-use. Each one is invalidated the moment it&apos;s accepted.
                  </div>
                </div>
              )}

              {mfaError && (
                <div
                  data-testid="login-mfa-error"
                  className="text-xs text-rose-400 mt-2"
                >
                  {mfaError}
                </div>
              )}

              <button
                type="button"
                data-testid="login-use-recovery-toggle"
                onClick={() => {
                  setUseRecovery((v) => !v);
                  setMfaCode("");
                  setRecoveryCode("");
                  setMfaError("");
                }}
                className="mt-3 text-xs text-slate-400 hover:text-emerald-400 underline underline-offset-2"
              >
                {useRecovery
                  ? "Use my authenticator app instead"
                  : "Use a recovery code instead"}
              </button>
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid="login-submit"
            className="btn-primary w-full inline-flex items-center justify-center gap-2"
          >
            {busy ? "Signing in…" : mfaStage ? <>Verify &amp; sign in <ArrowRight className="h-4 w-4" /></> : <>Sign in <ArrowRight className="h-4 w-4" /></>}
          </button>
        </form>

        <div className="mt-4 flex items-center justify-between text-sm">
          <Link to="/forgot" data-testid="login-forgot-link" className="text-slate-400 hover:text-emerald-400">
            Forgot password?
          </Link>
          <Link to="/register" className="text-slate-400 hover:text-emerald-400" data-testid="login-to-register">
            Create account
          </Link>
        </div>
      </div>

      <div className="hidden md:block relative bg-[#0A0D12] border-l border-white/8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 via-transparent to-transparent" />
        <div className="absolute bottom-10 left-10 right-10">
          <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-3">Today&apos;s signal</div>
          <div className="font-heading text-3xl leading-tight max-w-md">
            &ldquo;Sample record: 12-unit multifamily, tax delinquent, absentee owner. Illustrative score <span className="text-emerald-400 font-mono">87</span>.&rdquo;
          </div>
          <div className="text-slate-500 mt-4 text-sm">Subject-to and master-lease candidates surfaced daily.</div>
        </div>
      </div>
    </div>
  );
}
